import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, PaperclipIcon, SendIcon, UserIcon, FileIcon, ImageIcon, FileTextIcon, MusicIcon, VideoIcon, MessageSquareIcon, XIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Conversation, Message, getUserConversations, subscribeToUserConversations, subscribeToConversationMessages, sendMessage, markMessagesAsRead, debugCheckFirestoreConversations } from '../../services/messagingService';
import { useTheme } from '../providers/ThemeProvider';
export const TeacherMessages = () => {
  const {
    currentUser,
    userData
  } = useAuth();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messageText, setMessageText] = useState('');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Track whether messages are loading for the selected conversation (prevents empty-state flash)
  const [messagesLoading, setMessagesLoading] = useState(false);
  // Pending attachment state for preview before send
  const [pendingAttachment, setPendingAttachment] = useState<{
    file: File;
    type: 'image' | 'pdf' | 'audio' | 'video' | 'doc';
    name: string;
    previewUrl?: string;
  } | null>(null);
  // Lightbox state for image viewer
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  // Add refs for message scrolling
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeConversationIdRef = useRef<string | null>(null);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // DIAGNOSTIC: Log teacher user information when component mounts
  useEffect(() => {
    if (currentUser) {
      console.group('🔍 DIAGNOSTIC: Teacher User Information');
      console.log('Teacher Auth UID:', currentUser.uid);
      console.log('Teacher Email:', currentUser.email);
      console.log('Teacher Display Name:', currentUser.displayName);
      console.log('Teacher User Data from Context:', userData);
      console.groupEnd();
      // DIAGNOSTIC: Check Firestore for conversations related to this teacher
      debugCheckFirestoreConversations(currentUser.uid);
    } else {
      console.warn('🔍 DIAGNOSTIC: No authenticated teacher user found');
    }
  }, [currentUser, userData]);
  // Load conversations when component mounts
  useEffect(() => {
    if (!currentUser) {
      console.warn('🔍 DIAGNOSTIC: Cannot load conversations - no authenticated user');
      setLoading(false);
      setError('Please log in to view your messages');
      return;
    }
    const isMountedRef = {
      current: true
    };
    setLoading(true);
    setError(null);
    console.log('🔍 DIAGNOSTIC: Loading conversations for teacher:', currentUser.uid);
    // First, load conversations immediately (non-realtime)
    getUserConversations(currentUser.uid, 'teacher').then(initialConversations => {
      console.log(`Initial load: Found ${initialConversations.length} conversations`);
      // Filter out conversations with missing critical fields
      const validConversations = initialConversations.filter(conv => conv.id && conv.teacherId && conv.studentId);
      if (validConversations.length < initialConversations.length) {
        console.warn(`Filtered out ${initialConversations.length - validConversations.length} invalid conversations`);
      }
      // Sort conversations by lastMessageTimestamp (newest first)
      const sortedConversations = sortConversationsByTimestamp(validConversations);
      if (isMountedRef.current) {
        setConversations(sortedConversations);
        setLoading(false);
        // Don't set an error for empty conversations - it's a valid state
        if (error) setError(null);
        if (currentUser && sortedConversations.length > 0) {
          // Set first conversation as selected by default
          if (!selectedConversation) {
            setSelectedConversation(sortedConversations[0]);
            activeConversationIdRef.current = sortedConversations[0].id;
          }
        }
        // Retry if initial fetch returned empty (may be a transient rules/auth issue)
        if (sortedConversations.length === 0 && currentUser) {
          console.log('[TeacherMessages] Initial fetch empty, retrying in 1.5s...');
          retryTimeoutRef.current = setTimeout(() => {
            if (!isMountedRef.current) return;
            getUserConversations(currentUser.uid, 'teacher').then(retryConversations => {
              if (!isMountedRef.current) return;
              console.log(`[TeacherMessages] Retry found ${retryConversations.length} conversations`);
              const validRetry = retryConversations.filter(conv => conv.id && conv.teacherId && conv.studentId);
              const sortedRetry = sortConversationsByTimestamp(validRetry);
              if (sortedRetry.length > 0) {
                setConversations(sortedRetry);
                if (!selectedConversation) {
                  setSelectedConversation(sortedRetry[0]);
                  activeConversationIdRef.current = sortedRetry[0].id;
                }
              }
            }).catch(err => console.warn('[TeacherMessages] Retry failed:', err));
          }, 1500);
        }
      }
    }).catch(err => {
      console.error('Error loading initial conversations:', err);
      if (isMountedRef.current) {
        setError('Failed to load conversations. Please try refreshing the page.');
        setLoading(false);
        // Retry after error
        console.log('[TeacherMessages] Initial fetch errored, retrying in 2s...');
        retryTimeoutRef.current = setTimeout(() => {
          if (!isMountedRef.current) return;
          getUserConversations(currentUser.uid, 'teacher').then(retryConversations => {
            if (!isMountedRef.current) return;
            console.log(`[TeacherMessages] Error retry found ${retryConversations.length} conversations`);
            const validRetry = retryConversations.filter(conv => conv.id && conv.teacherId && conv.studentId);
            const sortedRetry = sortConversationsByTimestamp(validRetry);
            if (sortedRetry.length > 0) {
              setConversations(sortedRetry);
              setError(null);
              if (!selectedConversation) {
                setSelectedConversation(sortedRetry[0]);
                activeConversationIdRef.current = sortedRetry[0].id;
              }
            }
          }).catch(err2 => console.warn('[TeacherMessages] Error retry also failed:', err2));
        }, 2000);
      }
    });
    // Then set up real-time listener for ongoing updates
    console.log('🔍 DIAGNOSTIC: Setting up conversation subscription for teacher:', currentUser.uid);
    const unsubscribe = subscribeToUserConversations(currentUser.uid, fetchedConversations => {
      console.log('🔍 DIAGNOSTIC: Received conversations update:', fetchedConversations.length);
      // Filter out conversations with missing critical fields
      const validConversations = fetchedConversations.filter(conv => conv.id && conv.teacherId && conv.studentId);
      // Sort conversations by lastMessageTimestamp (newest first)
      const sortedConversations = sortConversationsByTimestamp(validConversations);
      if (isMountedRef.current) {
        // Only update conversations if we received valid data
        // Prevent onSnapshot from overwriting existing conversations with empty results
        setConversations(prev => sortedConversations.length > 0 ? sortedConversations : prev);
        // Set first conversation as active if none is selected
        if (validConversations.length > 0 && !selectedConversation) {
          setSelectedConversation(validConversations[0]);
          activeConversationIdRef.current = validConversations[0].id;
        } else if (selectedConversation) {
          // Update the selected conversation if it's in the new list
          const updatedSelectedConversation = validConversations.find(conv => conv.id === selectedConversation.id);
          if (updatedSelectedConversation) {
            setSelectedConversation(updatedSelectedConversation);
          }
        }
        // Clear any error since we successfully got conversations
        if (error) setError(null);
        // Ensure loading is set to false
        if (loading) setLoading(false);
      }
    }, 'teacher');
    return () => {
      console.log('🔍 DIAGNOSTIC: Cleaning up conversation subscription');
      isMountedRef.current = false;
      unsubscribe();
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [currentUser]);
  // Helper function to sort conversations by timestamp
  const sortConversationsByTimestamp = (conversations: Conversation[]): Conversation[] => {
    return [...conversations].sort((a, b) => {
      // Get timestamp values, properly handling Firestore timestamps
      const getTimestampValue = (timestamp: any): number => {
        // For Firestore timestamps with toDate method
        if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
          try {
            return timestamp.toDate().getTime();
          } catch (e) {
            console.error('Error converting Firestore timestamp:', e);
            return 0;
          }
        }
        // For Date objects
        if (timestamp instanceof Date) {
          return timestamp.getTime();
        }
        // For numeric timestamps
        if (typeof timestamp === 'number') {
          return timestamp;
        }
        // Default case - use 0 to ensure consistent sorting
        return 0;
      };
      const timeA = getTimestampValue(a.lastMessageTimestamp);
      const timeB = getTimestampValue(b.lastMessageTimestamp);
      // Sort in descending order (newest first)
      return timeB - timeA;
    });
  };
  // Subscribe to messages for selected conversation
  useEffect(() => {
    if (!selectedConversation) {
      setMessages([]);
      activeConversationIdRef.current = null;
      return;
    }
    // Validate the selected conversation has all required fields
    if (!selectedConversation.id || !selectedConversation.teacherId || !selectedConversation.studentId) {
      console.error('Selected conversation is missing critical fields:', selectedConversation);
      setError('Invalid conversation data. Please try selecting another conversation.');
      return;
    }
    console.log(`🔥 REALTIME: Setting up message subscription for conversation: ${selectedConversation.id}`);
    activeConversationIdRef.current = selectedConversation.id;
    setMessagesLoading(true); // Show loading state until first snapshot
    let isFirstSnapshot = true;
    // Set up real-time listener for messages
    const unsubscribe = subscribeToConversationMessages(selectedConversation.id, fetchedMessages => {
      console.log(`🔥 REALTIME: Received ${fetchedMessages.length} messages for conversation ${selectedConversation.id}`, fetchedMessages);
      // Only update messages if this is still the active conversation
      if (activeConversationIdRef.current === selectedConversation.id) {
        console.log(`🔥 REALTIME: Updating messages state with ${fetchedMessages.length} messages for active conversation ${selectedConversation.id}`);
        // Sort messages by timestamp in ascending order (oldest first)
        const sortedMessages = [...fetchedMessages].sort((a, b) => {
          // Get timestamp values, properly handling Firestore timestamps
          const getTimestampValue = (timestamp: any): number => {
            // For Firestore timestamps with toDate method
            if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
              try {
                return timestamp.toDate().getTime();
              } catch (e) {
                console.error('Error converting Firestore timestamp:', e);
                return 0;
              }
            }
            // For Date objects
            if (timestamp instanceof Date) {
              return timestamp.getTime();
            }
            // For numeric timestamps
            if (typeof timestamp === 'number') {
              return timestamp;
            }
            // Default case - use 0 to ensure consistent sorting
            return 0;
          };
          const timeA = getTimestampValue(a.timestamp);
          const timeB = getTimestampValue(b.timestamp);
          // Sort in ascending order (oldest first)
          return timeA - timeB;
        });
        // Force React to recognize state change by creating a new array reference
        setMessages([...sortedMessages]);
        // Clear loading state and scroll to bottom
        if (isFirstSnapshot) {
          isFirstSnapshot = false;
          setMessagesLoading(false);
          // Defer scroll until after React renders the messages and opacity transition starts
          requestAnimationFrame(() => {
            if (messageContainerRef.current) {
              messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
            }
            // Follow-up scroll to catch any late layout
            setTimeout(() => {
              if (messageContainerRef.current) {
                messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
              }
            }, 150);
          });
        } else {
          // Subsequent snapshots (new messages arriving) — scroll to bottom
          requestAnimationFrame(() => {
            if (messageContainerRef.current) {
              messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
            }
          });
        }
      } else {
        console.log(`🔥 REALTIME: Ignoring messages update for conversation ${selectedConversation.id} because active conversation is ${activeConversationIdRef.current}`);
      }
    });
    // Mark messages as read when conversation becomes active
    if (currentUser) {
      markMessagesAsRead(selectedConversation.id, currentUser.uid).catch(err => console.error('Error marking messages as read:', err));
    }
    return () => {
      console.log(`🔥 REALTIME: Unsubscribing from messages for conversation: ${selectedConversation.id}`);
      unsubscribe();
    };
  }, [selectedConversation?.id, currentUser?.uid]);
  // Handle selecting a conversation
  const handleSelectConversation = (conversation: Conversation) => {
    console.log(`🔥 REALTIME: Selecting conversation: ${conversation.id}`);
    // Set new conversation directly — no null intermediate state
    setSelectedConversation(conversation);
    activeConversationIdRef.current = conversation.id;
    setMessages([]); // Clear messages when switching conversations
    setMessagesLoading(true); // Show loading state until first snapshot arrives
    // Mark messages as read
    if (currentUser) {
      markMessagesAsRead(conversation.id, currentUser.uid).catch(err => console.error('Error marking messages as read:', err));
    }
  };
  // Handle sending a new message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const hasText = messageText.trim().length > 0;
    const hasAttachment = !!pendingAttachment;
    if (!hasText && !hasAttachment || !selectedConversation || !currentUser) {
      console.log('Cannot send message - missing required data:', {
        hasMessageText: hasText,
        hasAttachment,
        hasSelectedConversation: !!selectedConversation,
        hasCurrentUser: !!currentUser
      });
      return;
    }
    try {
      const receiverId = selectedConversation.studentId;
      if (!selectedConversation.id) {
        console.error('Missing conversation ID for message send');
        throw new Error('Missing conversation ID');
      }
      if (!currentUser.uid) {
        console.error('Missing sender ID (current user) for message send');
        throw new Error('Missing sender ID');
      }
      if (!receiverId) {
        console.error('Missing receiver ID (student) for message send');
        throw new Error('Missing receiver ID');
      }
      console.log('🔍 DIAGNOSTIC: Sending message:', {
        conversationId: selectedConversation.id,
        senderId: currentUser.uid,
        receiverId: receiverId,
        text: messageText,
        hasAttachment
      });
      // Clear input immediately for better UX
      const messageToSend = messageText.trim();
      const attachmentToSend = pendingAttachment;
      setMessageText('');
      setPendingAttachment(null);
      // Clean up image preview URL
      if (attachmentToSend?.previewUrl) {
        URL.revokeObjectURL(attachmentToSend.previewUrl);
      }
      setIsUploading(hasAttachment);
      try {
        await sendMessage(selectedConversation.id, currentUser.uid, receiverId, messageToSend || undefined, attachmentToSend ? {
          file: attachmentToSend.file,
          type: attachmentToSend.type
        } : undefined);
        console.log('Message sent successfully');
        setTimeout(() => {
          if (messageContainerRef.current) {
            messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
          }
        }, 50);
      } catch (sendError) {
        console.error('Error in sendMessage function:', sendError);
        const errorMessage = sendError instanceof Error ? `Failed to send message: ${sendError.message}` : 'Failed to send message. Please try again.';
        alert(errorMessage);
        throw sendError;
      } finally {
        setIsUploading(false);
      }
    } catch (error) {
      console.error('🔍 DIAGNOSTIC: Error sending message:', error);
      if (error instanceof Error) {
        console.error('Error details:', error.message);
        console.error('Error stack:', error.stack);
      }
      setIsUploading(false);
    }
    return false;
  };
  // Handle file selection — store in state for preview, do NOT auto-send
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedConversation || !currentUser) return;
    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size exceeds 10MB limit');
      return;
    }
    // Determine file type
    let attachmentType: 'image' | 'pdf' | 'audio' | 'video' | 'doc' = 'doc';
    if (file.type.startsWith('image/')) attachmentType = 'image';else if (file.type.startsWith('audio/')) attachmentType = 'audio';else if (file.type.startsWith('video/')) attachmentType = 'video';else if (file.type === 'application/pdf') attachmentType = 'pdf';
    // Create preview URL for images
    let previewUrl: string | undefined;
    if (attachmentType === 'image') {
      previewUrl = URL.createObjectURL(file);
    }
    // Clean up previous preview URL if any
    if (pendingAttachment?.previewUrl) {
      URL.revokeObjectURL(pendingAttachment.previewUrl);
    }
    setPendingAttachment({
      file,
      type: attachmentType,
      name: file.name,
      previewUrl
    });
    // Reset file input so the same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  // Remove pending attachment
  const handleRemoveAttachment = () => {
    if (pendingAttachment?.previewUrl) {
      URL.revokeObjectURL(pendingAttachment.previewUrl);
    }
    setPendingAttachment(null);
  };
  // Helper to get icon for attachment type
  const getAttachmentIcon = (type: string) => {
    switch (type) {
      case 'image':
        return <ImageIcon className="h-4 w-4" data-id="element-8788" />;
      case 'pdf':
        return <FileTextIcon className="h-4 w-4 text-red-500" data-id="element-8789" />;
      case 'audio':
        return <MusicIcon className="h-4 w-4 text-purple-500" data-id="element-8790" />;
      case 'video':
        return <VideoIcon className="h-4 w-4 text-blue-500" data-id="element-8791" />;
      default:
        return <FileIcon className="h-4 w-4 text-blue-500" data-id="element-8792" />;
    }
  };
  // Trigger file input click
  const handleAttachmentClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  // Filter conversations by search query
  const filteredConversations = conversations.filter(conversation => {
    if (!searchQuery) return true;
    return conversation.studentName.toLowerCase().includes(searchQuery.toLowerCase());
  });
  // Safely format timestamp to prevent rendering issues
  const formatTimestamp = (timestamp: any): string => {
    if (!timestamp) return 'Just now';
    try {
      if (typeof timestamp === 'string') {
        return timestamp;
      }
      if (timestamp && typeof timestamp.toDate === 'function') {
        return timestamp.toDate().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return 'Just now';
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Just now';
    }
  };
  // Render attachment based on type
  const renderAttachment = (message: Message) => {
    if (!message.attachmentUrl) return null;
    switch (message.attachmentType) {
      case 'image':
        return <div className="mt-2 max-w-[280px] min-h-[120px] max-h-60 rounded-lg overflow-hidden bg-black/5 dark:bg-white/5" style={{
          transition: 'min-height 150ms ease'
        }} data-id="element-8793">
            <img src={message.attachmentUrl} alt="Attachment" className="w-full h-auto max-h-60 rounded-lg object-contain cursor-pointer hover:opacity-90 transition-opacity" loading="eager" onClick={() => setLightboxImage(message.attachmentUrl!)} onLoad={e => {
            // Smoothly transition min-height to actual image height then remove
            const container = e.currentTarget.parentElement;
            if (container) {
              const imgHeight = e.currentTarget.naturalHeight;
              const displayHeight = Math.min(imgHeight, 240); // max-h-60 = 240px
              container.style.minHeight = `${displayHeight}px`;
              requestAnimationFrame(() => {
                if (container) container.style.minHeight = 'auto';
              });
            }
          }} data-id="element-8794" />
          </div>;
      case 'pdf':
        return <a href={message.attachmentUrl} target="_blank" rel="noopener noreferrer" className={`flex items-center mt-2 p-2 rounded-lg ${isDark ? 'bg-navy-700/50' : 'bg-gray-100'}`} data-id="element-8795">
            <FileTextIcon className="h-6 w-6 mr-2 text-red-500" data-id="element-8796" />
            <span className={`text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-8797">
              {message.attachmentName || 'PDF Document'}
            </span>
          </a>;
      case 'audio':
        return <div className="mt-2" data-id="element-8798">
            <div className={`flex items-center p-2 rounded-lg ${isDark ? 'bg-navy-700/50' : 'bg-gray-100'}`} data-id="element-8799">
              <MusicIcon className="h-6 w-6 mr-2 text-purple-500" data-id="element-8800" />
              <span className={`text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-8801">
                {message.attachmentName || 'Audio File'}
              </span>
            </div>
            <audio controls className="mt-2 max-w-full" data-id="element-8802">
              <source src={message.attachmentUrl} data-id="element-8803" />
              Your browser does not support audio playback.
            </audio>
          </div>;
      case 'video':
        return <div className="mt-2" data-id="element-8804">
            <video controls className="max-w-full rounded-lg max-h-60" data-id="element-8805">
              <source src={message.attachmentUrl} data-id="element-8806" />
              Your browser does not support video playback.
            </video>
          </div>;
      default:
        return <a href={message.attachmentUrl} target="_blank" rel="noopener noreferrer" className={`flex items-center mt-2 p-2 rounded-lg ${isDark ? 'bg-navy-700/50' : 'bg-gray-100'}`} data-id="element-8807">
            <FileIcon className="h-6 w-6 mr-2 text-blue-500" data-id="element-8808" />
            <span className={`text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-8809">
              {message.attachmentName || 'Document'}
            </span>
          </a>;
    }
  };
  return <div className="w-full" data-id="element-8810">
      <div className="max-w-7xl mx-auto" data-id="element-8811">
        {/* Messages Interface */}
        <div className={`${isDark ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm overflow-hidden transition-colors duration-300`} data-id="element-8812">
          {loading ? <div className="flex items-center justify-center h-[calc(100svh-80px)]" data-id="element-8813">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" data-id="element-8814"></div>
            </div> : error ? <div className="flex items-center justify-center h-[calc(100svh-80px)]" data-id="element-8815">
              <div className="text-center max-w-md p-6" data-id="element-8816">
                <MessageSquareIcon className={`h-12 w-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-8817" />
                <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'} mb-2`} data-id="element-8818">
                  Error Loading Messages
                </h3>
                <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} mb-4`} data-id="element-8819">
                  {error}
                </p>
                <button onClick={() => window.location.reload()} className="bg-deep-pink hover:bg-burgundy text-white px-4 py-2 rounded-lg" data-id="element-8820">
                  Refresh Page
                </button>
              </div>
            </div> : <div className="flex h-[calc(100svh-80px)]" data-id="element-8821">
              {/* Conversations List */}
              <div className={`w-full md:w-1/3 border-r ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-id="element-8822">
                {/* Search */}
                <div className={`p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-id="element-8823">
                  <div className={`relative rounded-lg ${isDark ? 'bg-navy-900/50' : 'bg-gray-100'}`} data-id="element-8824">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8825">
                      <SearchIcon className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8826" />
                    </div>
                    <input type="text" placeholder="Search messages" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`block w-full pl-10 pr-3 py-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 ${isDark ? 'bg-navy-900/50 text-white placeholder-gray-400' : 'bg-gray-100 text-gray-900 placeholder-gray-500'} border-none`} data-id="element-8827" />
                  </div>
                </div>
                {/* Conversations */}
                <div className="overflow-y-auto h-full pb-20 px-2 py-2 space-y-1" data-id="element-8828">
                  {filteredConversations.length > 0 ? filteredConversations.map(conversation => <div key={conversation.id} onClick={() => handleSelectConversation(conversation)} className={`p-4 rounded-xl ${selectedConversation?.id === conversation.id ? isDark ? 'bg-navy-900/70' : 'bg-gray-100' : isDark ? 'hover:bg-navy-900/50' : 'hover:bg-gray-50'} cursor-pointer transition-colors duration-200`} data-id="element-8829">
                        <div className="flex items-start" data-id="element-8830">
                          <div className="relative" data-id="element-8831">
                            {/* Student avatar/image */}
                            <div className="h-12 w-12 rounded-full overflow-hidden mr-3" data-id="element-8832">
                              <img src={conversation.studentAvatar || 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png'} alt={conversation.studentName} className="h-full w-full object-cover" onError={e => {
                        e.currentTarget.src = '';
                        e.currentTarget.parentElement.innerHTML = `<div class="h-12 w-12 ${isDark ? 'bg-navy-700' : 'bg-gray-200'} flex items-center justify-center"><UserIcon class="h-6 w-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}" /></div>`;
                      }} data-id="element-8833" />
                            </div>
                            {conversation.unread && <div className="absolute top-0 right-2 h-3 w-3 bg-blue-500 rounded-full" data-id="element-8834"></div>}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-8835">
                            <div className="flex justify-between" data-id="element-8836">
                              <h3 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'} truncate`} data-id="element-8837">
                                {conversation.studentName}
                              </h3>
                              <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8838">
                                {formatTimestamp(conversation.lastMessageTimestamp)}
                              </span>
                            </div>
                            <p className={`text-xs truncate mt-1 ${conversation.unread ? isDark ? 'text-white font-medium' : 'text-gray-900 font-medium' : isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8839">
                              {conversation.lastMessage || 'Start a conversation'}
                            </p>
                          </div>
                        </div>
                      </div>) : <div className="flex flex-col items-center justify-center h-full p-8 text-center" data-id="element-8840">
                      <MessageSquareIcon className={`h-12 w-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-8841" />
                      <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-8842">
                        No conversations yet
                      </h3>
                      <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8843">
                        You don't have any messages yet. Students will appear
                        here when they message you.
                      </p>
                    </div>}
                </div>
              </div>
              {/* Message Content */}
              <div className="hidden md:flex md:w-2/3 flex-col" data-id="element-8844">
                {selectedConversation ? <>
                    {/* Conversation Header */}
                    <div className={`p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'} flex items-center`} data-id="element-8845">
                      <div className="flex items-center" data-id="element-8846">
                        <div className="h-10 w-10 rounded-full overflow-hidden mr-3" data-id="element-8847">
                          <img src={selectedConversation.studentAvatar || 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png'} alt={selectedConversation.studentName} className="h-full w-full object-cover" onError={e => {
                      e.currentTarget.src = '';
                      e.currentTarget.parentElement.innerHTML = `<div class="h-10 w-10 ${isDark ? 'bg-navy-700' : 'bg-gray-200'} flex items-center justify-center"><UserIcon class="h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}" /></div>`;
                    }} data-id="element-8848" />
                        </div>
                        <div data-id="element-8849">
                          <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-8850">
                            {selectedConversation.studentName}
                          </h3>
                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8851">
                            Student
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Messages */}
                    <div ref={messageContainerRef} className={`flex-1 overflow-y-auto p-4 ${isDark ? 'bg-navy-900/30' : 'bg-gray-50'} transition-opacity duration-200 ${messagesLoading ? 'opacity-0' : 'opacity-100'}`} data-id="element-8852">
                      {messagesLoading ? <div className="flex items-center justify-center h-full" data-id="element-8853">
                          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" data-id="element-8854"></div>
                        </div> : <div className="space-y-4" data-id="element-8855">
                          {messages.length > 0 ? messages.map(message => {
                    const isCurrentUser = message.senderId === currentUser?.uid;
                    return <div key={message.id} className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`} data-id="element-8856">
                                  <div className={`max-w-xs md:max-w-md lg:max-w-lg rounded-lg px-4 py-2 ${isCurrentUser ? 'bg-blue-500 text-white' : isDark ? 'bg-navy-800 text-white' : 'bg-white text-gray-800'} ${isCurrentUser ? '' : 'border'} ${isDark && !isCurrentUser ? 'border-white/10' : !isDark && !isCurrentUser ? 'border-gray-200' : ''}`} data-id="element-8857">
                                    {message.text && <p className="text-sm" data-id="element-8858">{message.text}</p>}
                                    {message.attachmentUrl && renderAttachment(message)}
                                    <p className={`text-xs mt-1 ${isCurrentUser ? 'text-blue-100' : isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8859">
                                      {formatTimestamp(message.timestamp)}
                                    </p>
                                  </div>
                                </div>;
                  }) : <div className="flex items-center justify-center h-full" data-id="element-8860">
                              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8861">
                                No messages yet. Start a conversation!
                              </p>
                            </div>}
                          {/* Add an empty div at the end to scroll to */}
                          <div ref={messageEndRef} data-id="element-8862" />
                        </div>}
                    </div>
                    {/* Message Input */}
                    <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-id="element-8863">
                      {/* Attachment Preview */}
                      {pendingAttachment && <div className={`px-4 pt-3 pb-1`} data-id="element-8864">
                          <div className={`flex items-center gap-3 p-2.5 rounded-lg ${isDark ? 'bg-navy-900/60 border border-white/10' : 'bg-gray-100 border border-gray-200'}`} data-id="element-8865">
                            {/* Image thumbnail or file icon */}
                            {pendingAttachment.type === 'image' && pendingAttachment.previewUrl ? <img src={pendingAttachment.previewUrl} alt="Preview" className="h-12 w-12 rounded-md object-cover flex-shrink-0" data-id="element-8866" /> : <div className={`h-10 w-10 rounded-md flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-8867">
                                {getAttachmentIcon(pendingAttachment.type)}
                              </div>}
                            {/* File name and size */}
                            <div className="flex-1 min-w-0" data-id="element-8868">
                              <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-8869">
                                {pendingAttachment.name}
                              </p>
                              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8870">
                                {(pendingAttachment.file.size / 1024).toFixed(0)}
                                KB •{' '}
                                {pendingAttachment.type.charAt(0).toUpperCase() + pendingAttachment.type.slice(1)}
                              </p>
                            </div>
                            {/* Remove button */}
                            <button type="button" onClick={handleRemoveAttachment} className={`p-1.5 rounded-full flex-shrink-0 transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-navy-600' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-200'}`} data-id="element-8871">
                              <XIcon className="h-4 w-4" data-id="element-8872" />
                            </button>
                          </div>
                        </div>}
                      {/* Input row */}
                      <div className="p-4 pt-2" data-id="element-8873">
                        <form onSubmit={handleSendMessage} className="flex items-center" data-id="element-8874">
                          <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.mp3,.mp4,.docx,.txt" data-id="element-8875" />
                          <button type="button" onClick={handleAttachmentClick} disabled={isUploading} className={`p-2 rounded-full ${isUploading ? 'opacity-50 cursor-not-allowed' : ''} ${isDark ? 'text-gray-400 hover:text-white hover:bg-navy-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'} transition-colors`} data-id="element-8876">
                            <PaperclipIcon className="h-5 w-5" data-id="element-8877" />
                          </button>
                          <input type="text" value={messageText} onChange={e => setMessageText(e.target.value)} placeholder={isUploading ? 'Sending...' : pendingAttachment ? 'Add a message (optional)...' : 'Type a message...'} disabled={isUploading} className={`flex-1 mx-3 py-2 px-4 rounded-full focus:outline-none focus:ring-1 focus:ring-blue-500 ${isUploading ? 'opacity-70' : ''} ${isDark ? 'bg-navy-900/50 text-white placeholder-gray-400 border-white/10' : 'bg-gray-100 text-gray-900 placeholder-gray-500 border-gray-200'} border`} data-id="element-8878" />
                          <button type="submit" disabled={!messageText.trim() && !pendingAttachment || isUploading} className={`p-2 rounded-full transition-all duration-150 ${(messageText.trim() || pendingAttachment) && !isUploading ? 'bg-blue-500 hover:bg-blue-600 text-white cursor-pointer shadow-sm' : isDark ? 'bg-navy-700 text-gray-500 cursor-default' : 'bg-gray-200 text-gray-400 cursor-default'}`} data-id="element-8879">
                            <SendIcon className="h-5 w-5" data-id="element-8880" />
                          </button>
                        </form>
                      </div>
                    </div>
                  </> : <div className="flex-1 flex items-center justify-center" data-id="element-8881">
                    <div className="text-center" data-id="element-8882">
                      <MessageSquareIcon className={`h-12 w-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-8883" />
                      <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-8884">
                        Select a conversation
                      </h3>
                      <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8885">
                        Choose a student from the list to view your conversation
                      </p>
                    </div>
                  </div>}
              </div>
            </div>}
        </div>
      </div>
      {/* Image Lightbox */}
      {lightboxImage && <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] animate-in fade-in duration-200" onClick={() => setLightboxImage(null)} onKeyDown={e => {
      if (e.key === 'Escape') setLightboxImage(null);
    }} tabIndex={0} ref={el => el?.focus()} data-id="element-8886">
          <button onClick={e => {
        e.stopPropagation();
        setLightboxImage(null);
      }} className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors z-10" aria-label="Close lightbox" data-id="element-8887">
            <XIcon className="h-8 w-8" data-id="element-8888" />
          </button>
          <img src={lightboxImage} alt="Full size attachment" className="max-w-[90%] max-h-[90%] rounded-lg object-contain" onClick={e => e.stopPropagation()} data-id="element-8889" />
        </div>}
    </div>;
};